import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToFirstScrollableAncestor,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import { Icon } from '@shared/icons';
import { Translate } from '@shared/translation';
import { isNonEmptyArray } from '@shared/utils/array';
import type { Column } from '@tanstack/react-table';
import { memo } from 'react';
import { Checkbox } from '../../Inputs';
import Popper from '../../Popper';
import { useDataProvider } from '../DataProviderContext';
import { useTableTexts } from '../useTableTexts';

export const ConfigureColumnsAction = () => (
  // Separating components fixes re-rendering of the action, keeping the popper anchor stable.
  <ConfigureColumns />
);

const ConfigureColumns = memo(function ConfigureColumns() {
  const texts = useTableTexts();

  return (
    <Popper
      placement="bottom-end"
      Trigger={({ onClick }) => (
        <Button onClick={onClick} color="grey" variant="contained">
          <Translate text={texts.columnSelectionButton} />
        </Button>
      )}
      Content={Content}
    />
  );
});

const Content = () => {
  const { table } = useDataProvider();
  const sensors = useSensors(
    useSensor(TouchSensor),
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );

  if (table === undefined) {
    return <>No table</>;
  }

  const tableInstance = table;
  const allColumns = tableInstance.getAllColumns();
  const lastColumn = tableInstance.getVisibleFlatColumns().length === 1;
  const columnIds = isNonEmptyArray(tableInstance.state.columnOrder)
    ? [...tableInstance.state.columnOrder]
    : allColumns.map((col) => col.id);
  const orderedColumns = [...allColumns].sort(
    (a, b) => columnIds.indexOf(a.id) - columnIds.indexOf(b.id)
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (
      active.id !== over?.id &&
      typeof active.id === 'string' &&
      typeof over?.id === 'string'
    ) {
      const oldIndex = columnIds.indexOf(active.id);
      const newIndex = columnIds.indexOf(over.id);
      tableInstance.setColumnOrder(arrayMove(columnIds, oldIndex, newIndex));
    }
  }

  return (
    <Paper
      elevation={8}
      sx={{
        maxHeight: '95vh',
        overflow: 'auto',
      }}
    >
      <div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[
            restrictToVerticalAxis,
            restrictToFirstScrollableAncestor,
          ]}
        >
          <SortableContext
            items={orderedColumns}
            strategy={verticalListSortingStrategy}
          >
            <List dense>
              {orderedColumns.map((column) => (
                <ColumnItem
                  column={column}
                  lastColumn={lastColumn}
                  key={column.id}
                />
              ))}
            </List>
          </SortableContext>
        </DndContext>
      </div>
    </Paper>
  );
};

interface ColumnItemProps {
  column: Column<any, any>;
  lastColumn: boolean;
}

function ColumnItem({ column, lastColumn }: ColumnItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: column.id });

  const style = {
    touchAction: 'none',
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <ListItem
      disablePadding
      style={style}
      ref={setNodeRef}
      secondaryAction={
        <div {...attributes} {...listeners}>
          <Box sx={{ cursor: 'grab' }}>
            <Icon name="drag_indicator" size="small" />
          </Box>
        </div>
      }
    >
      <ListItemButton onClick={() => column.toggleVisibility()}>
        <ListItemIcon sx={{ minWidth: 'unset' }}>
          <Checkbox
            checked={column.getIsVisible()}
            disabled={lastColumn && column.getIsVisible()}
            disableRipple
          />
        </ListItemIcon>
        <ListItemText>{column.columnDef.meta?.name ?? column.id}</ListItemText>
      </ListItemButton>
    </ListItem>
  );
}
